import PlainLoadingSpinner from 'components/PlainLoadingSpinner';
import { useEnterStore } from 'hooks/stores/useEnterStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { useLoggedEffect } from 'hooks/useLoggedEffect';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { SOURCE_QUERY_PARAM } from 'pages/validate-3ds-payment';
import { useEffect, useRef } from 'react';
import { Message } from 'semantic-ui-react';
import { trackEecCheckoutOption } from 'utils/analytics/analytics';

import AdyenDropInCheckout from './AdyenDropInCheckout';

export const AdyenCheckoutWrapper = observer(
  (props: { checkout2026?: boolean }) => {
    const router = useRouter();

    const {
      orderLoaded,
      orderId,
      isValid,
      haveValidPaymentSession,
      adyenPaymentSession,
      confirmed,
        setOrderError,
      orderError,
      navigateToCheckout,
      checkOrderStatusAndAdvanceToConfirmation,
      maybeContinueToPayment,
      currentPromo,
      initialised,
    } = useEnterStore();

    const { userInitialized: userStoreInitialised, currentUser } =
      useUserStore();
    // Holds a reference to the timer that periodically checks for order completion.
    // Needed so we can clear it when the component unmounts.
    const orderCompletionTimerRef = useRef<any>(null);
    const isPollingRef = useRef(false);

    useEffect(() => {
      return () => {
        if (orderCompletionTimerRef.current) {
          clearTimeout(orderCompletionTimerRef.current);
        }
      };
    }, []);

    useEffect(() => {
      if (haveValidPaymentSession && !isPollingRef.current) {
        isPollingRef.current = true;
        checkOrderStatusAndAdvanceToConfirmation(orderCompletionTimerRef);
      }
    }, [haveValidPaymentSession]);

    useEffect(() => {
      (async () => {
        if (!isValid) {
          router.replace('/enternow/1');
        } else {
          if (userStoreInitialised && !currentUser) {
            await navigateToCheckout();
          }
        }
      })();
    }, [isValid, userStoreInitialised, currentUser, router]);

    useLoggedEffect(
      'AdyenCheckoutWrapper continue to payment',
      () => {
        if (!haveValidPaymentSession && isValid && currentUser && initialised) {
          maybeContinueToPayment();
        }
      },
      [
        haveValidPaymentSession,
        isValid,
        currentUser,
        orderId,
        currentPromo || false,
        initialised,
      ],
    );

    if (!isValid) {
      return <Message error>Order not valid</Message>;
    }
    if (!confirmed) {
      return <Message error>Order not confirmed</Message>;
    }

    if (!haveValidPaymentSession || !adyenPaymentSession) {
      return (
        <>
          {orderError && orderError.length > 0 && (
            <div style={{ margin: props.checkout2026 ? undefined : '10px' }}>
              <div
                style={{ height: props.checkout2026 ? undefined : '20px' }}
              />
              <Message
                id="errorMessage"
                negative
                style={{ width: '100%', maxWidth: '500px', fontSize: '12px' }}
              >
                {orderError}
              </Message>
            </div>
          )}
          {!orderError ||
            (orderError.length <= 0 && (
              <PlainLoadingSpinner style={{ margin: '2em auto' }} />
            ))}
        </>
      );
    }

    return (
      <div style={{ marginLeft: 'auto', marginRight: 'auto' }}>
        {orderError && orderError.length > 0 && (
          <div style={{ margin: props.checkout2026 ? undefined : '10px' }}>
            <>
              <div
                style={{ height: props.checkout2026 ? undefined : '20px' }}
              />
              <Message
                id="errorMessage"
                negative
                style={{ width: '100%', maxWidth: '500px', fontSize: '12px' }}
              >
                {orderError}
              </Message>
            </>
          </div>
        )}
        {orderLoaded && adyenPaymentSession && (
          <AdyenDropInCheckout
            key={adyenPaymentSession?.adyenPaymentSessionId || 'unknownsession'}
            queryParamsObject={{
              source: SOURCE_QUERY_PARAM.STANDARD_TICKET_ORDER,
            }}
            paymentSession={adyenPaymentSession}
            onLoadingStateChange={(loading: boolean) => {
              // Handle loading state if needed
            }}
            completionHandler={async () => {
              await trackEecCheckoutOption('Card');
            }}
            displayError={async (error: string | null) => {
              setOrderError(error);
            }}
            style={{
              fontSize: '12px',
              width: '100%',
              maxWidth: 500,
            }}
            initialRedirectResult={undefined}
            checkout2026={props.checkout2026}
          />
        )}
      </div>
    );
  },
);
